import React, { useCallback, useState } from 'react';

import { DIAGNOSTIC_RATING } from 'libs/constants';

import { supabase, useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';

import { Button } from 'components/ui/Button';
import { Slider } from 'components/ui/Slider';

import { Fonts, spacing } from 'styles/theme';
import { CardBottomButtonWrapper, NestedCard, Bumper } from 'styles/shared';

interface SubmitDiagnosticProps {
    onSubmit: () => Promise<void>;
}

export const SubmitDiagnostic = (props: SubmitDiagnosticProps) => {
    const { onSubmit } = props;

    const { userId } = useAuth();
    const { showSuccessToast, showErrorToast } = useToast();

    const [growthMindset, setGrowthMindset] = useState<string>('');
    const [balanceMeaning, setBalanceMeaning] = useState<string>('');
    const [emotionalIntelligence, setEmotionalIntelligence] =
        useState<string>('');
    const [effectiveCommunication, setEffectiveCommunication] =
        useState<string>('');
    const [selfKnowledge, setSelfKnowledge] = useState<string>('');
    const [feedback, setFeedback] = useState<string>('');
    const [conflictResolution, setConflictResolution] = useState<string>('');
    const [delegation, setDelegation] = useState<string>('');
    const [leadershipConfidence, setLeadershipConfidence] =
        useState<string>('');
    const [coaching, setCoaching] = useState<string>('');
    const [isSubmittingDiagnostic, setIsSubmittingDiagnostic] =
        useState<boolean>(false);

    const onChangeGrowthMindset = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const target = event.target as HTMLSelectElement;
            setGrowthMindset(target.value);
        },
        []
    );

    const onChangeBalanceMeaning = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const target = event.target as HTMLSelectElement;
            setBalanceMeaning(target.value);
        },
        []
    );

    const onChangeEmotionalIntelligence = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const target = event.target as HTMLSelectElement;
            setEmotionalIntelligence(target.value);
        },
        []
    );

    const onChangeEffectiveCommunication = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const target = event.target as HTMLSelectElement;
            setEffectiveCommunication(target.value);
        },
        []
    );

    const onChangeSelfKnowledge = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const target = event.target as HTMLSelectElement;
            setSelfKnowledge(target.value);
        },
        []
    );

    const onChangeFeedback = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const target = event.target as HTMLSelectElement;
            setFeedback(target.value);
        },
        []
    );

    const onChangeConflictResolution = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const target = event.target as HTMLSelectElement;
            setConflictResolution(target.value);
        },
        []
    );

    const onChangeDelegation = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const target = event.target as HTMLSelectElement;
            setDelegation(target.value);
        },
        []
    );

    const onChangeLeadershipConfidence = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const target = event.target as HTMLSelectElement;
            setLeadershipConfidence(target.value);
        },
        []
    );

    const onChangeCoaching = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const target = event.target as HTMLSelectElement;
            setCoaching(target.value);
        },
        []
    );

    const internalOnSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (isSubmittingDiagnostic) {
                return;
            }
            setIsSubmittingDiagnostic(true);

            const { error } = await supabase.from('diagnostics').insert({
                created_by: userId,
                growth_mindset: growthMindset,
                balance_meaning: balanceMeaning,
                emotional_intelligence: emotionalIntelligence,
                effective_communication: effectiveCommunication,
                self_knowledge: selfKnowledge,
                feedback,
                conflict_resolution: conflictResolution,
                delegation,
                leadership_confidence: leadershipConfidence,
                coaching,
            });

            if (error == null) {
                showSuccessToast('Successfully submitted diagnostic!');
                onSubmit();
            } else {
                showErrorToast(error.message);
            }

            setIsSubmittingDiagnostic(false);
        },
        [
            isSubmittingDiagnostic,
            userId,
            growthMindset,
            balanceMeaning,
            emotionalIntelligence,
            effectiveCommunication,
            selfKnowledge,
            feedback,
            conflictResolution,
            delegation,
            leadershipConfidence,
            coaching,
            showSuccessToast,
            onSubmit,
            showErrorToast,
        ]
    );

    return (
        <>
            <Fonts.Heading5>{`Diagnostic Survey`}</Fonts.Heading5>
            <Bumper />
            <Fonts.Medium>
                {`Each of the competencies in the leadership diagnostic relate to topics and conversations designed into The Grand experience.`}
            </Fonts.Medium>
            <Bumper />
            <Fonts.Medium>
                {`Toward the end of your journey, you'll revisit this diagnostic and see what areas you made progress in and what areas you might want to focus on in coaching next.`}
            </Fonts.Medium>
            <Bumper />
            <form onSubmit={internalOnSubmit}>
                <NestedCard>
                    <Fonts.Small
                        $isOpenSansSemiBold
                    >{`Growth Mindset:`}</Fonts.Small>
                    <Slider
                        onInput={onChangeGrowthMindset}
                        value={growthMindset || undefined}
                        label="I approach challenges as opportunities for personal growth and learning."
                        values={DIAGNOSTIC_RATING as unknown as Array<string>}
                        required
                    />
                    <Bumper />
                    <Fonts.Small
                        $isOpenSansSemiBold
                    >{`Balance / Meaning:`}</Fonts.Small>
                    <Slider
                        onInput={onChangeBalanceMeaning}
                        value={balanceMeaning || undefined}
                        label="I have a clear sense of what's important in my life and how that influences the decisions I make."
                        values={DIAGNOSTIC_RATING as unknown as Array<string>}
                        required
                    />
                    <Bumper />
                    <Fonts.Small
                        $isOpenSansSemiBold
                    >{`Emotional Intelligence:`}</Fonts.Small>
                    <Slider
                        onInput={onChangeEmotionalIntelligence}
                        value={emotionalIntelligence || undefined}
                        label="I have the capacity to express my feelings and understand others' emotions at work."
                        values={DIAGNOSTIC_RATING as unknown as Array<string>}
                        required
                    />
                    <Bumper />
                    <Fonts.Small
                        $isOpenSansSemiBold
                    >{`Effective Communication:`}</Fonts.Small>
                    <Slider
                        onInput={onChangeEffectiveCommunication}
                        value={effectiveCommunication || undefined}
                        label="I feel equipped to articulate my ideas and express needs with my team."
                        values={DIAGNOSTIC_RATING as unknown as Array<string>}
                        required
                    />
                    <Bumper />
                    <Fonts.Small
                        $isOpenSansSemiBold
                    >{`Self Knowledge / Awareness:`}</Fonts.Small>
                    <Slider
                        onInput={onChangeSelfKnowledge}
                        value={selfKnowledge || undefined}
                        label="I feel I have a strong understanding of myself."
                        values={DIAGNOSTIC_RATING as unknown as Array<string>}
                        required
                    />
                    <Bumper />
                    <Fonts.Small $isOpenSansSemiBold>{`Feedback:`}</Fonts.Small>
                    <Slider
                        onInput={onChangeFeedback}
                        value={feedback || undefined}
                        label="I am comfortable giving and receiving feedback."
                        values={DIAGNOSTIC_RATING as unknown as Array<string>}
                        required
                    />
                    <Bumper />
                    <Fonts.Small
                        $isOpenSansSemiBold
                    >{`Conflict Resolution:`}</Fonts.Small>
                    <Slider
                        onInput={onChangeConflictResolution}
                        value={conflictResolution || undefined}
                        label="I am comfortable resolving conflicts with my team."
                        values={DIAGNOSTIC_RATING as unknown as Array<string>}
                        required
                    />
                    <Bumper />
                    <Fonts.Small
                        $isOpenSansSemiBold
                    >{`Delegation:`}</Fonts.Small>
                    <Slider
                        onInput={onChangeDelegation}
                        value={delegation || undefined}
                        label="I am comfortable delegating responsibilities."
                        values={DIAGNOSTIC_RATING as unknown as Array<string>}
                        required
                    />
                    <Bumper />
                    <Fonts.Small
                        $isOpenSansSemiBold
                    >{`Leadership Confidence:`}</Fonts.Small>
                    <Slider
                        onInput={onChangeLeadershipConfidence}
                        value={leadershipConfidence || undefined}
                        label="I am confident in my ability to effectively lead and inspire a team."
                        values={DIAGNOSTIC_RATING as unknown as Array<string>}
                        required
                    />
                    <Bumper />
                    <Fonts.Small $isOpenSansSemiBold>{`Coaching:`}</Fonts.Small>
                    <Slider
                        onInput={onChangeCoaching}
                        value={coaching || undefined}
                        label="I have the coaching skills and tools to do my work well."
                        values={DIAGNOSTIC_RATING as unknown as Array<string>}
                        required
                    />
                </NestedCard>
                <Bumper $minHeight={spacing.xLarge} />
                <CardBottomButtonWrapper>
                    <Button
                        type="submit"
                        value="Submit"
                        $isLoading={isSubmittingDiagnostic}
                    >
                        {`Submit`}
                    </Button>
                </CardBottomButtonWrapper>
            </form>
        </>
    );
};
